/**
 * Geçmiş sohbetler.
 *
 * Masaüstü panelinde liste sürekli görünür ve dar panelde sohbetin yarısını yiyordu;
 * burada açılır bir katman: açıkken tam genişlik, seçim yapınca kapanıyor. Mobilde de
 * aynı bileşen tam ekran çalışıyor.
 *
 * Başlık sunucuda ilk mesajdan üretiliyor; kullanıcı yeniden adlandırabiliyor.
 */
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Check, MessageSquare, Pencil, Search, Trash2, X } from 'lucide-react';
import { cn } from '@/shared/lib/cn';
import { Input } from '@/shared/ui/Input';
import { IconButton } from '@/shared/ui/IconButton';
import { Spinner } from '@/shared/ui/Spinner';
import { dialogs } from '@/shared/ui/dialogStore';
import { relativeTime } from '@/features/worker/model/workerTask';
import type { AiConversation } from '@/shared/types/aiChat';

export function AiHistoryList({
  conversations,
  loading,
  activeId,
  onSelect,
  onRename,
  onDelete,
}: {
  conversations: AiConversation[];
  loading: boolean;
  activeId: string | null;
  onSelect: (conversation: AiConversation) => void;
  onRename: (conversationId: string, title: string) => void;
  onDelete: (conversationId: string) => void;
}) {
  const { t } = useTranslation();
  const [search, setSearch] = useState('');
  const [editing, setEditing] = useState<{ id: string; title: string } | null>(null);

  const filtered = search.trim()
    ? conversations.filter((c) =>
        (c.title ?? '').toLocaleLowerCase('tr').includes(search.trim().toLocaleLowerCase('tr')),
      )
    : conversations;

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <div className="border-border relative border-b p-2">
        <Search className="text-fg-subtle pointer-events-none absolute top-1/2 left-4 h-4 w-4 -translate-y-1/2" />
        <Input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder={t('ai.searchConversations')}
          aria-label={t('ai.searchConversations')}
          className="pl-8"
        />
      </div>

      <div className="min-h-0 flex-1 overflow-auto p-1">
        {loading ? (
          <div className="flex justify-center py-6">
            <Spinner />
          </div>
        ) : filtered.length === 0 ? (
          <p className="text-fg-subtle px-3 py-6 text-center text-sm">{t('ai.noConversations')}</p>
        ) : (
          <ul className="space-y-0.5">
            {filtered.map((conversation) => {
              const isEditing = editing?.id === conversation.conversationId;
              return (
                <li key={conversation.conversationId}>
                  {isEditing ? (
                    <div className="flex items-center gap-1 px-1 py-1">
                      <Input
                        autoFocus
                        value={editing.title}
                        onChange={(e) => setEditing({ ...editing, title: e.target.value })}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') {
                            onRename(conversation.conversationId, editing.title.trim());
                            setEditing(null);
                          } else if (e.key === 'Escape') setEditing(null);
                        }}
                        className="min-w-0 flex-1"
                        aria-label={t('ai.rename')}
                      />
                      <IconButton
                        label={t('common:Save')}
                        size="sm"
                        onClick={() => {
                          onRename(conversation.conversationId, editing.title.trim());
                          setEditing(null);
                        }}
                      >
                        <Check className="h-4 w-4" />
                      </IconButton>
                      <IconButton label={t('common:Cancel')} size="sm" onClick={() => setEditing(null)}>
                        <X className="h-4 w-4" />
                      </IconButton>
                    </div>
                  ) : (
                    <div
                      className={cn(
                        'group hover:bg-bg-hover flex items-center gap-2 rounded-md px-2 py-1.5',
                        conversation.conversationId === activeId && 'bg-brand-500/10',
                      )}
                    >
                      <button
                        type="button"
                        onClick={() => onSelect(conversation)}
                        className="flex min-w-0 flex-1 items-center gap-2 text-left"
                      >
                        <MessageSquare className="text-fg-subtle h-4 w-4 shrink-0" />
                        <span className="min-w-0 flex-1">
                          <span className="block truncate text-sm">
                            {conversation.title || t('ai.untitled')}
                          </span>
                          <span className="text-fg-subtle block text-[11px]">
                            {relativeTime(conversation.lastMessageAt ?? null)}
                            {conversation.messageCount
                              ? ` · ${t('ai.messageCount', { count: conversation.messageCount })}`
                              : ''}
                          </span>
                        </span>
                      </button>
                      {/* Dokunmatikte hover yok: düğmeler mobilde hep görünür. */}
                      <span className="flex shrink-0 items-center gap-0.5 opacity-100 sm:opacity-0 sm:group-hover:opacity-100 sm:focus-within:opacity-100">
                        <IconButton
                          label={t('ai.rename')}
                          size="sm"
                          onClick={() =>
                            setEditing({
                              id: conversation.conversationId,
                              title: conversation.title ?? '',
                            })
                          }
                        >
                          <Pencil className="h-3.5 w-3.5" />
                        </IconButton>
                        <IconButton
                          label={t('common:Delete')}
                          size="sm"
                          onClick={() => {
                            void (async () => {
                              const ok = await dialogs.confirm({
                                title: t('ai.deleteTitle'),
                                message: t('ai.deleteConfirm', {
                                  name: conversation.title || t('ai.untitled'),
                                }),
                                kind: 'delete',
                              });
                              if (ok) onDelete(conversation.conversationId);
                            })();
                          }}
                        >
                          <Trash2 className="text-danger h-3.5 w-3.5" />
                        </IconButton>
                      </span>
                    </div>
                  )}
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </div>
  );
}
